/**
 * InstancedMeshComponent encapsulates a single THREE.InstancedMesh for actors.
 * Renders multiple instances of the same geometry and material efficiently.
 * Extends BaseInstancedMeshComponent for single geometry-material pair usage.
 *
 * Supports opt-in LOD via the `lods` property. LOD0 is always the component's
 * primary `geometry`/`modelMaterial`; each `LODEntry` in `lods` becomes an
 * additional tier selected at (or past) its `distance` from the camera.
 */
import * as THREE from 'three';

import { EngineClass } from '../../systems/ClassRegistry.js';
import { property } from '../../utils/serialization/decorator.js';

import { BaseInstancedMeshComponent, LODEntry } from './BaseInstancedMeshComponent.js';

import type { BaseInstancedMeshComponentOptions, GeometryMaterialPair } from './BaseInstancedMeshComponent.js';
import type { GenericMaterialType } from '../../materials.js';
import type { EditorPropertyChangedResult } from '../../utils/EditorClassMeta.js';

export interface InstancedMeshComponentOptions extends BaseInstancedMeshComponentOptions {
  geometry?: THREE.BufferGeometry;
  material?: GenericMaterialType;
  lods?: LODEntry[];
}

@EngineClass('InstancedMeshComponent')
export class InstancedMeshComponent extends BaseInstancedMeshComponent {



  @property()
  protected geometry: THREE.BufferGeometry = new THREE.BoxGeometry(1, 1, 1);

  @property()
  protected modelMaterial: GenericMaterialType = new THREE.MeshStandardMaterial();

  /** Extra LOD tiers. LOD0 is synthesized from `geometry` + `modelMaterial` at distance 0. */
  @property()
  public lods: LODEntry[] = [];



  private reconstructInstances() {
    // Build LOD grid: each LOD is a single-submesh row.
    const sortedLods = [...this.lods].sort((a, b) => a.distance - b.distance);
    const lodPairs: GeometryMaterialPair[][] = [
      [{ geometry: this.geometry, material: this.modelMaterial }],
    ];
    const distances: number[] = [0];
    for (const lod of sortedLods) {
      if (lod.distance <= 0) {
        console.warn('InstancedMeshComponent: ignoring LOD entry with non-positive distance', lod);
        continue;
      }
      lodPairs.push([{
        geometry: lod.geometry,
        material: lod.material ?? this.modelMaterial,
      }]);
      distances.push(lod.distance);
    }

    this.populateInstances(lodPairs, distances);
  }

  public override initialize(options?: InstancedMeshComponentOptions): void {
    super.initialize(options);

    this.geometry = options?.geometry ?? this.geometry;
    this.modelMaterial = options?.material ?? this.modelMaterial;
    this.lods = options?.lods ?? this.lods;
  }

  public override postLoad(): void {
    super.postLoad();
  }

  public override beginPlay(): void {
    // Ensure instances are populated before super.beginPlay is called, because super.beginPlay adds it to physics
    this.reconstructInstances();

    super.beginPlay();
  }

  public override getInstancedMesh(): THREE.InstancedMesh | undefined {
    return super.getInstancedMesh(0);
  }

  public override getMaterial(): THREE.Material | THREE.Material[] | undefined {
    return super.getMaterial(0);
  }

  public override setGeometry(geometry: THREE.BufferGeometry): void {
    this.geometry = geometry;
    this.reconstructInstances();
  }

  public override setMaterial(material: GenericMaterialType): void {
    this.modelMaterial = material;
    this.reconstructInstances();
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override onEditorAddToWorld(): void {
    super.onEditorAddToWorld();
    // Ensure the instanced mesh is populated in the editor
    this.reconstructInstances();
  }

  public override onEditorPropertyChanged(path: string, value: any, result: EditorPropertyChangedResult): void {
    if (path === 'modelMaterial') {
      this.setMaterial(value);
    } else if (path === 'geometry') {
      this.setGeometry(value);
    } else if (path === 'lods') {
      this.reconstructInstances();
    }
    super.onEditorPropertyChanged(path, value, result);
  }

  public override getEditorClassIcon(): string | null {
    return 'Icon_InstancedMesh';
  }
}
